import api from './api';
import { normalizePermissionKeys } from '@/utils/permissionKeys';
import type { TenantStaff, EcomShopAssignment } from '@/mocks/staffs';
import type { StaffListQuery } from './queryKeys';
import type { AmisEmployeeEntry, AmisEmployeeSelection } from '@/modules/accounting-sync/services/amis-employee.service';
import { resolveAssignedChannels, type B2BChannelType } from '@tcr-erp/shared';

export interface StaffListResponse {
  data: TenantStaff[];
  meta: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface ActivityLog {
  _id: string;
  at: string;
  action: string;
  resource: string;
  detail: string;
  ip?: string;
  device?: string;
}

export interface ActivityLogListResponse {
  data: ActivityLog[];
  total: number;
  stats?: {
    logins: number;
    orders: number;
    today: number;
  };
}

export interface ActivityLogListQuery {
  page?: number;
  page_size?: number;
  from?: string;
  to?: string;
}


export interface CreateStaffPayload {
  name: string;
  email: string;
  phone: string;
  role: TenantStaff['role'];
  storeId?: string;
  storeAssignments?: string[];
  /** TCR-2075 */
  additionalRoleAliases?: string[];
  assignedB2BChannels?: B2BChannelType[];
}

export type UpdateStaffPayload = Partial<{
  name: string;
  email: string;
  phone: string;
  role: TenantStaff['role'];
  status: TenantStaff['status'];
  storeId: string;
  storeAssignments: string[];
  /** RBAC B2B — chỉ áp dụng khi role đích = `b2b_sales`, validate ở BE (Task 6). */
  salesManagerId: string | null;
  /** RBAC ECom — chỉ áp dụng khi role đích = `nv_sale_ecom`, validate ở BE (Task 2). */
  ecomSalesManagerId: string | null;
  /**
   * RBAC ECom (Task 6) — danh sách shop cụ thể được gán, không validate role đích ở BE
   * (khác `ecomSalesManagerId`) — xem `user.service.ts#updateUser`, field này chỉ set
   * thẳng vào `user.ecom_shop_assignments` nếu có mặt trong DTO, bất kể `dto.role` là gì.
   * VÌ VẬY `StaffDrawer.tsx` chỉ được đưa field này vào payload khi form thực sự có giá
   * trị (Form.Item `preserve={false}`, chỉ mount khi role = `nv_sale_ecom`) — nếu gửi `[]`
   * vô điều kiện ở MỌI lần PATCH sẽ âm thầm xoá sạch gán shop của NV khi sửa các field
   * khác không liên quan.
   */
  ecomShopAssignments: EcomShopAssignment[];
  /**
   * TCR-1934 (Task 8) — kênh kinh doanh (MT/GT/SI) gán riêng cho staff, khớp
   * `User.assigned_b2b_channel` ở BE (Task 1). KHÔNG validate theo role đích (khác
   * `salesManagerId`) — bất kỳ role nào cũng gán được, BE chỉ dùng field này để scope dữ
   * liệu khi đọc (Task 2-7). `null` = tường minh xoá gán (allowClear ở Select).
   */
  assignedB2BChannel: B2BChannelType | null;
  /** TCR-2075 — vai trò bổ sung (alias). */
  additionalRoleAliases: string[];
  /** TCR-2075 — nhiều Kênh B2B. */
  assignedB2BChannels: B2BChannelType[];
  /** TCR-2492 — chỉ gửi khi người dùng đổi ô; `amis_refid: null` = gỡ map. */
  amisEmployees: AmisEmployeeSelection[];
}>;

function toUserQuery(q: StaffListQuery): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (q.page) params.page = q.page;
  if (q.page_size) params.limit = q.page_size;
  if (q.search) params.search = q.search;
  if (q.role_id) params.role = q.role_id;
  if (q.role_alias) params.role_alias = q.role_alias;
  if (q.status) params.status = q.status;
  if (q.store_id) params.store_id = q.store_id;
  // TCR-2492 — phải whitelist ở đây, nếu không filter không tới được BE.
  if (q.amis_status) params.amis_status = q.amis_status;
  return params;
}

export function mapUserToStaff(raw: Record<string, unknown>, index?: number): TenantStaff {
  // BE returns staff_id = _id.toString() — use _id as primary source
  const id = raw._id ?? raw.staff_id ?? raw.user_id ?? raw.id;
  const assignedStores = Array.isArray(raw.assigned_stores)
    ? raw.assigned_stores
        .map((s) => {
          const item = s as Record<string, unknown>;
          const storeId = item.store_id ?? item._id;
          if (!storeId) return null;
          return {
            storeId: String(storeId),
            code: item.code == null ? null : String(item.code),
            name: item.name == null ? null : String(item.name),
          };
        })
        .filter(Boolean) as TenantStaff['assignedStores']
    : undefined;
  return {
    staff_id: String(id ?? (index !== undefined ? `staff-${index}` : '')),
    staff_code: (raw.staff_code as string) ?? null,
    name: String(raw.name ?? raw.full_name ?? ''),
    email: String(raw.email ?? ''),
    phone: String(raw.phone ?? ''),
    role: (raw.role_alias ?? raw.role ?? 'viewer') as TenantStaff['role'],
    status: (raw.status ?? 'active') as TenantStaff['status'],
    storeId: String(raw.store_id ?? ''),
    storeName: String(raw.store_name ?? ''),
    assignedStores,
    assignedStoreIds: Array.isArray(raw.assigned_store_ids)
      ? raw.assigned_store_ids.map(String)
      : assignedStores?.map((s) => s.storeId),
    // RBAC B2B (Task 6/7) — BE trả `sales_manager_id` (snake_case, ADR-013) trong mọi
    // response GET/PATCH staff; chỉ có giá trị khi role = b2b_sales.
    salesManagerId: (raw.sales_manager_id as string | null | undefined) ?? null,
    // RBAC ECom (Task 3) — BE trả `ecom_sales_manager_id` (snake_case, cùng pattern
    // sales_manager_id ở trên) trong mọi response GET/PATCH staff; chỉ có giá trị khi
    // role = nv_sale_ecom.
    ecomSalesManagerId: (raw.ecom_sales_manager_id as string | null | undefined) ?? null,
    // RBAC ECom (Task 6) — BE trả `ecom_shop_assignments` (mảng `{channel, shop_id}`,
    // snake_case) trong mọi response GET/PATCH staff; luôn mảng (rỗng nếu chưa gán), không
    // `null`/`undefined` — xem `user.service.ts`'s `toUserResponse()`.
    // TCR-1609 — `shop_name` được `resolveEcomShopNames()` hydrate thêm vào từng item ở
    // GET /users/:id; map qua nếu có, giữ `null` khi BE không hydrate được (không suy ra
    // chuỗi rỗng để phân biệt với "chưa từng có tên").
    ecomShopAssignments: Array.isArray(raw.ecom_shop_assignments)
      ? raw.ecom_shop_assignments.map((a) => {
          const item = a as Record<string, unknown>;
          return {
            channel: String(item.channel) as EcomShopAssignment['channel'],
            shop_id: String(item.shop_id),
            shop_name: item.shop_name == null ? null : String(item.shop_name),
          };
        })
      : [],
    // RBAC — BE trả quyền hiệu lực đã resolve từ role trong `permission_keys`.
    permissionKeys: Array.isArray(raw.permission_keys)
      ? normalizePermissionKeys(raw.permission_keys.filter((key): key is string => typeof key === 'string'))
      : [],
    // TCR-1925 — chỉ `GET /users/:id` trả 2 field này; `undefined` ở response list
    // là bình thường, KHÔNG suy ra mảng rỗng (phân biệt "không có dữ liệu" với "rỗng thật").
    // `rolePermissionKeys` chuẩn hoá qua `normalizePermissionKeys` vì đến từ catalogue vai
    // trò (có thể lẫn key chưa chuẩn dạng chấm); `extraPermissionKeys` KHÔNG chuẩn hoá lại ở
    // FE vì backend đã `normalizePermissionKey` trước khi lưu vào `extra_permission_keys`
    // (xem hợp đồng PATCH /users/:id/permissions) — chuẩn hoá 2 lần là thừa, không phải bug.
    rolePermissionKeys: Array.isArray(raw.role_permission_keys)
      ? normalizePermissionKeys(raw.role_permission_keys.filter((key): key is string => typeof key === 'string'))
      : undefined,
    extraPermissionKeys: Array.isArray(raw.extra_permission_keys)
      ? raw.extra_permission_keys.filter((key): key is string => typeof key === 'string')
      : undefined,
    avatar: raw.avatar as string | undefined,
    lastLoginAt: String(raw.last_login_at ?? ''),
    createdAt: String(raw.created_at ?? ''),
    updatedAt: String(raw.updated_at ?? ''),
    // TCR-1791 — chỉ `GET /users/:id` trả 2 field này (xem comment ở TenantStaff['isLocked']);
    // `undefined` ở response list là bình thường, KHÔNG suy ra false.
    isLocked: typeof raw.is_locked === 'boolean' ? raw.is_locked : undefined,
    lockUntil: raw.lock_until == null ? null : String(raw.lock_until),
    // TCR-1934 (Task 8) — BE trả `assigned_b2b_channel` (snake_case, Task 1) ở mọi response
    // GET/PATCH staff; `null`/thiếu field đều có nghĩa "chưa gán kênh nào".
    assignedB2BChannel: (raw.assigned_b2b_channel as B2BChannelType | null | undefined) ?? null,
    // TCR-2075 — BE trả 2 mảng này ở mọi response staff.
    additionalRoleAliases: Array.isArray(raw.additional_role_aliases)
      ? raw.additional_role_aliases.filter((a): a is string => typeof a === 'string')
      : [],
    assignedB2BChannels: resolveAssignedChannels(
      raw as { assigned_b2b_channels?: string[] | null; assigned_b2b_channel?: string | null },
    ),
    // TCR-2492 — mỗi tổ chức AMIS đang kết nối một phần tử; thiếu field = BE cũ/chưa kết nối.
    amisEmployee: Array.isArray(raw.amis_employee) ? (raw.amis_employee as AmisEmployeeEntry[]) : [],
  };
}

export const staffService = {
  list: (params: StaffListQuery) =>
    api.get<StaffListResponse>('/users', { params: toUserQuery(params) }).then((r) => {
      const d = r.data;
      const mapped = d.data.map((u, idx) => mapUserToStaff(u as unknown as Record<string, unknown>, idx));
      const seen = new Set<string>();
      const deduped = mapped.filter((s) => {
        if (!s.staff_id) return false;
        if (seen.has(s.staff_id)) return false;
        seen.add(s.staff_id);
        return true;
      });
      // TCR-1595: giữ nguyên `meta.total`/`totalPages` từ BE (đếm bằng countDocuments trên
      // toàn bộ tenant, không phải chỉ trang hiện tại) — trước đây bị ghi đè bằng
      // `deduped.length` (số dòng của RIÊNG trang này, luôn <= page_size), khiến
      // antd Pagination tính totalPages = 1 và không bao giờ hiển thị được trang 2 dù
      // tổng số nhân viên thật sự nhiều hơn page_size.
      return { ...d, data: deduped };
    }),

  /**
   * Lấy TOÀN BỘ tài khoản, gọi nhiều trang rồi ghép lại.
   *
   * Backend chặn `limit > 100` (trả 400). Trước đây màn chi tiết nhân viên xin
   * thẳng 500 bản ghi nên request hỏng mỗi lần vào trang, và ô "Chọn user ERP"
   * lúc nào cũng rỗng.
   *
   * Tải hết về một lần là cố ý: ô chọn lọc theo tên ở phía client
   * (`optionFilterProp="label"`), nên thiếu trang nào là gõ tên người ở trang đó
   * sẽ không ra kết quả.
   */
  listAll: async (params: Omit<StaffListQuery, 'page' | 'page_size'> = {}): Promise<TenantStaff[]> => {
    const PAGE_SIZE = 100;
    const first = await staffService.list({ ...params, page: 1, page_size: PAGE_SIZE });
    const totalPages = Math.max(1, first.meta?.total_pages ?? 1);
    const rest = totalPages > 1
      ? await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) => (
          staffService.list({ ...params, page: index + 2, page_size: PAGE_SIZE })
        )),
      )
      : [];
    // `list` chỉ khử trùng TRONG một trang; ghép nhiều trang phải khử lại lần nữa.
    const seen = new Set<string>();
    return [first, ...rest]
      .flatMap((page) => page.data)
      .filter((staff) => {
        if (!staff.staff_id || seen.has(staff.staff_id)) return false;
        seen.add(staff.staff_id);
        return true;
      });
  },

  getById: (id: string) =>
    api.get<{ data: Record<string, unknown> }>(`/users/${id}`).then((r) => mapUserToStaff(r.data.data, 0)),

  create: (body: CreateStaffPayload) => {
    const payload: Record<string, unknown> = {
      full_name: body.name,
      email: body.email,
      phone: body.phone,
      role: body.role,
    };
    if (body.storeId) payload.store_id = body.storeId;
    if (body.storeAssignments?.length) payload.store_assignments = body.storeAssignments;
    if (body.additionalRoleAliases?.length) payload.additional_role_aliases = body.additionalRoleAliases;
    if (body.assignedB2BChannels?.length) payload.assigned_b2b_channels = body.assignedB2BChannels;
    return api.post<{ data: Record<string, unknown> }>('/users/invite', payload).then((r) => mapUserToStaff(r.data.data, 0));
  },

  update: (id: string, body: UpdateStaffPayload) => {
    const payload: Record<string, unknown> = {};
    if (body.name !== undefined) payload.full_name = body.name;
    if (body.email !== undefined) payload.email = body.email;
    if (body.phone !== undefined) payload.phone = body.phone;
    if (body.role !== undefined) payload.role = body.role;
    if (body.status !== undefined) payload.status = body.status;
    if (body.storeId !== undefined) payload.store_id = body.storeId;
    if (body.storeAssignments !== undefined) payload.store_assignments = body.storeAssignments;
    // RBAC B2B (Task 6) — BE validate role đích phải là `b2b_sales_manager`; gửi `null`
    // để tường minh xoá gán (allowClear ở Select), không chỉ omit field.
    if (body.salesManagerId !== undefined) payload.sales_manager_id = body.salesManagerId;
    // RBAC ECom (Task 2) — BE validate role đích phải là `ecom_sales_manager`; gửi `null`
    // để tường minh xoá gán (allowClear ở Select), không chỉ omit field.
    if (body.ecomSalesManagerId !== undefined) payload.ecom_sales_manager_id = body.ecomSalesManagerId;
    // RBAC ECom (Task 6) — chỉ đưa vào payload khi caller thực sự truyền field này
    // (StaffDrawer.tsx chỉ truyền khi role đích = nv_sale_ecom, xem comment
    // UpdateStaffPayload['ecomShopAssignments'] ở trên) — omit hoàn toàn field khỏi payload
    // khi undefined để không vô tình xoá gán shop hiện có của các staff khác.
    if (body.ecomShopAssignments !== undefined) payload.ecom_shop_assignments = body.ecomShopAssignments;
    // TCR-1934 (Task 8) — gửi `null` tường minh khi bấm clear (allowClear ở Select), cùng
    // pattern `salesManagerId`/`ecomSalesManagerId` ở trên — không chỉ omit field khỏi payload.
    if (body.assignedB2BChannel !== undefined) payload.assigned_b2b_channel = body.assignedB2BChannel;
    if (body.additionalRoleAliases !== undefined) payload.additional_role_aliases = body.additionalRoleAliases;
    if (body.assignedB2BChannels !== undefined) payload.assigned_b2b_channels = body.assignedB2BChannels;
    if (body.amisEmployees !== undefined) payload.amis_employees = body.amisEmployees;
    return api.patch<{ data: Record<string, unknown> }>(`/users/${id}`, payload).then((r) => mapUserToStaff(r.data.data, 0));
  },

  updateStoreAssignments: (id: string, storeAssignments: string[]) =>
    api.patch<{ data: Record<string, unknown> }>(`/user/${id}/store-assignments`, { store_assignments: storeAssignments }).then((r) => r.data),

  /** TCR-1925 — owner cấp quyền bổ sung riêng. Mảng rỗng = gỡ hết quyền bổ sung. */
  updatePermissions: (id: string, extraPermissionKeys: string[]) =>
    api.patch<{ data: { extra_permission_keys: string[] } }>(
      `/users/${id}/permissions`,
      { extra_permission_keys: extraPermissionKeys },
    ).then((r) => r.data),


  activate: (id: string) =>
    api.post<{ data: Record<string, unknown> }>(`/users/${id}/activate`).then((r) => mapUserToStaff(r.data.data, 0)),

  suspend: (id: string) =>
    api.post<{ data: Record<string, unknown> }>(`/users/${id}/suspend`).then((r) => mapUserToStaff(r.data.data, 0)),

  resetPassword: (id: string) =>
    api.post<{ data: { message: string } }>(`/users/${id}/reset-password`).then((r) => r.data),

  // TCR-1660 — admin đặt trực tiếp mật khẩu mới, không qua email.
  setPassword: (id: string, password: string) =>
    api.post<{ data: { message: string } }>(`/users/${id}/set-password`, { password }).then((r) => r.data),

  // TCR-1791 — mở khoá tài khoản bị brute-force lockout (Redis). CHỈ owner có quyền
  // `staff.account_unlock` gọi được route này (BE gác đúng 1 key, không OR staff.update).
  unlockAccount: (id: string) =>
    api.post<{ data: Record<string, unknown> }>(`/users/${id}/unlock`).then((r) => mapUserToStaff(r.data.data, 0)),

  toggleStatus: (id: string, status: TenantStaff['status']) =>
    status === 'active'
      ? api.post<{ data: Record<string, unknown> }>(`/users/${id}/activate`).then((r) => mapUserToStaff(r.data.data, 0))
      : api.post<{ data: Record<string, unknown> }>(`/users/${id}/suspend`).then((r) => mapUserToStaff(r.data.data, 0)),

  delete: (id: string) =>
    api.delete<{ data: { message: string } }>(`/users/${id}`).then((r) => r.data),

  getActivityLog: (id: string, params: ActivityLogListQuery) =>
    api.get<ActivityLogListResponse>(`/users/${id}/activities`, { params }).then((r) => {
      // Map the backend ActionLog format to frontend ActivityLog format
      const rawData = (r.data.data || []) as any[];
      return {
        data: rawData.map(log => ({
          _id: log._id,
          at: log.created_at,
          action: log.event,
          resource: log.model,
          detail: log.description || '',
          ip: log.ip_address,
          device: log.user_agent,
        })),
        total: r.data.total || 0,
        stats: r.data.stats || { logins: 0, orders: 0, today: 0 },
      };
    }),
};
